import { get, post, put, del, patch } from '../utils/request';

// ==================== Type Definitions ====================

/**
 * Expense type enum
 */
export enum ExpenseType {
  CONSULTATION_FEE = 'CONSULTATION_FEE',
  LAWYER_FEE = 'LAWYER_FEE',
  CONSULTANT_FEE = 'CONSULTANT_FEE',
  SERVICE_FEE = 'SERVICE_FEE',
  TRAVEL_FEE = 'TRAVEL_FEE',
  OTHER = 'OTHER',
}

/**
 * Payment nature enum
 */
export enum PaymentNature {
  RECEIVED = 'RECEIVED',
  RECEIVABLE = 'RECEIVABLE',
  REFUND = 'REFUND',
}

/**
 * Attachment type enum
 */
export enum AttachmentType {
  INVOICE = 'INVOICE',
  ATTACHMENT = 'ATTACHMENT',
}

/**
 * Finance attachment interface
 */
export interface FinanceAttachment {
  id: number;
  finance_record_id: number;
  attachment_type: AttachmentType | string;
  file_name: string;
  file_size: number;
  file_type: string;
  cos_key: string;
  cos_url?: string;
  uploaded_by: number;
  created_at: string;
}

/**
 * Entity type enum
 */
export enum EntityType {
  LEAD = 'LEAD',
  CASE = 'CASE',
}

/**
 * Finance record interface
 */
export interface FinanceRecord {
  id: number;
  entity_type: EntityType;
  entity_id: number;
  expense_type: ExpenseType;
  payment_nature: PaymentNature;
  amount: string | number;
  payment_time?: string;
  remark?: string;
  created_by: number;
  created_at: string;
  updated_at?: string;
  deleted_at?: string;
  deleted_by?: number;
  creator_name?: string;
  deleter_name?: string;
  attachments: FinanceAttachment[];
}

/**
 * Finance summary interface
 */
export interface FinanceSummary {
  contract_amount?: string | number;
  total_received: string | number;
  total_refund: string | number;
  total_receivable: string | number;
  net_received: string | number;
}

/**
 * Finance record list response interface
 */
export interface FinanceRecordListResponse {
  items: FinanceRecord[];
  total: number;
}

/**
 * Create finance record request interface
 */
export interface CreateFinanceRecordRequest {
  entity_type: EntityType;
  entity_id: number;
  expense_type: ExpenseType;
  payment_nature: PaymentNature;
  amount: number;
  payment_time?: string;
  remark?: string;
  milestone_id?: number; // 关联的付费节点ID
}

/**
 * Update contract amount request interface
 */
export interface UpdateContractAmountRequest {
  contract_amount: number;
}

/**
 * Attachment download URL response interface
 */
export interface AttachmentDownloadUrlResponse {
  download_url: string;
  expires_in: number;
}

/**
 * Delete response interface
 */
export interface DeleteResponse {
  message: string;
  deleted_at?: string;
}

// ==================== API Functions ====================

/**
 * Get finance summary for an entity (lead or case)
 * @param entityType - Entity type (lead or case)
 * @param entityId - Entity ID
 * @returns Finance summary data
 */
export const getFinanceSummary = async (
  entityType: EntityType,
  entityId: number
): Promise<FinanceSummary> => {
  try {
    const basePath = entityType === EntityType.LEAD ? 'leads' : 'cases';
    return await get<FinanceSummary>(`/${basePath}/${entityId}/finance/summary`);
  } catch (error: any) {
    console.error('Failed to get finance summary:', error);
    throw error;
  }
};

/**
 * Get finance records for an entity (lead or case)
 * @param entityType - Entity type (lead or case)
 * @param entityId - Entity ID
 * @param skip - Number of records to skip (pagination)
 * @param limit - Maximum number of records to return
 * @param paymentNature - Optional filter by payment nature
 * @returns Finance record list
 */
export const getFinanceRecords = async (
  entityType: EntityType,
  entityId: number,
  skip: number = 0,
  limit: number = 100,
  paymentNature?: PaymentNature
): Promise<FinanceRecordListResponse> => {
  try {
    const basePath = entityType === EntityType.LEAD ? 'leads' : 'cases';
    const params: any = { skip, limit };
    if (paymentNature) {
      params.payment_nature = paymentNature;
    }
    
    return await get<FinanceRecordListResponse>(`/${basePath}/${entityId}/finance/records`, {
      params,
    });
  } catch (error: any) {
    console.error('Failed to get finance records:', error);
    throw error;
  }
};

/**
 * Create a new finance record
 * @param entityType - Entity type (lead or case)
 * @param entityId - Entity ID
 * @param data - Finance record data
 * @returns Created finance record
 */
export const createFinanceRecord = async (
  entityType: EntityType,
  entityId: number,
  data: Omit<CreateFinanceRecordRequest, 'entity_type' | 'entity_id'>
): Promise<FinanceRecord> => {
  try {
    const basePath = entityType === EntityType.LEAD ? 'leads' : 'cases';
    const requestData: CreateFinanceRecordRequest = {
      ...data,
      entity_type: entityType,
      entity_id: entityId,
    };
    
    return await post<FinanceRecord>(`/${basePath}/${entityId}/finance/records`, requestData);
  } catch (error: any) {
    console.error('Failed to create finance record:', error);
    throw error;
  }
};

/**
 * Delete a finance record (soft delete)
 * @param entityType - Entity type (lead or case)
 * @param entityId - Entity ID
 * @param recordId - Finance record ID
 * @returns Delete response
 */
export const deleteFinanceRecord = async (
  entityType: EntityType,
  entityId: number,
  recordId: number
): Promise<DeleteResponse> => {
  try {
    const basePath = entityType === EntityType.LEAD ? 'leads' : 'cases';
    return await del<DeleteResponse>(`/${basePath}/${entityId}/finance/records/${recordId}`);
  } catch (error: any) {
    console.error('Failed to delete finance record:', error);
    throw error;
  }
};

/**
 * Update contract amount for an entity (lead or case)
 * @param entityType - Entity type (lead or case)
 * @param entityId - Entity ID
 * @param contractAmount - Contract amount
 * @returns Updated data
 */
export const updateContractAmount = async (
  entityType: EntityType,
  entityId: number,
  contractAmount: number
): Promise<any> => {
  try {
    const data: UpdateContractAmountRequest = {
      contract_amount: contractAmount,
    };
    
    if (entityType === EntityType.LEAD) {
      return await put<any>(`/leads/${entityId}/contract-amount`, data);
    } else {
      return await put<any>(`/cases/${entityId}/finance/contract-amount`, data);
    }
  } catch (error: any) {
    console.error('Failed to update contract amount:', error);
    throw error;
  }
};

/**
 * Upload an attachment to a finance record
 * @param entityType - Entity type (lead or case)
 * @param entityId - Entity ID
 * @param recordId - Finance record ID
 * @param file - File to upload
 * @returns Created attachment
 */
export const uploadAttachment = async (
  entityType: EntityType,
  entityId: number,
  recordId: number,
  file: File
): Promise<FinanceAttachment> => {
  try {
    // Validate file size (max 20MB)
    const maxSize = 20 * 1024 * 1024; // 20MB in bytes
    if (file.size > maxSize) {
      throw new Error('文件大小不能超过20MB');
    }
    
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    
    if (!allowedTypes.includes(file.type)) {
      throw new Error('不支持的文件格式，请上传PDF、图片、Word或Excel文件');
    }
    
    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    
    // Upload file
    const basePath = entityType === EntityType.LEAD ? 'leads' : 'cases';
    return await post<FinanceAttachment>(
      `/${basePath}/${entityId}/finance/records/${recordId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  } catch (error: any) {
    console.error('Failed to upload attachment:', error);
    throw error;
  }
};

/**
 * Get download URL for an attachment
 * @param entityType - Entity type (lead or case)
 * @param entityId - Entity ID
 * @param attachmentId - Attachment ID
 * @returns Download URL response
 */
export const getAttachmentDownloadUrl = async (
  entityType: EntityType,
  entityId: number,
  attachmentId: number
): Promise<AttachmentDownloadUrlResponse> => {
  try {
    const basePath = entityType === EntityType.LEAD ? 'leads' : 'cases';
    return await get<AttachmentDownloadUrlResponse>(
      `/${basePath}/${entityId}/finance/attachments/${attachmentId}/download`
    );
  } catch (error: any) {
    console.error('Failed to get attachment download URL:', error);
    throw error;
  }
};

/**
 * Delete an attachment
 * @param entityType - Entity type (lead or case)
 * @param entityId - Entity ID
 * @param attachmentId - Attachment ID
 * @returns Delete response
 */
export const deleteAttachment = async (
  entityType: EntityType,
  entityId: number,
  attachmentId: number
): Promise<DeleteResponse> => {
  try {
    const basePath = entityType === EntityType.LEAD ? 'leads' : 'cases';
    return await del<DeleteResponse>(
      `/${basePath}/${entityId}/finance/attachments/${attachmentId}`
    );
  } catch (error: any) {
    console.error('Failed to delete attachment:', error);
    throw error;
  }
};

/**
 * Get all finance records (leads and cases)
 * @param params - Query parameters
 * @returns Finance record list
 */
export interface GetAllFinanceRecordsParams {
  skip?: number;
  limit?: number;
  case_name?: string;
  contact_name?: string;
  contact_phone?: string;
  expense_type?: ExpenseType;
  payment_nature?: PaymentNature;
  payment_time_start?: string;
  payment_time_end?: string;
  responsible_person_id?: number;
}

export interface FinanceRecordWithDetails extends FinanceRecord {
  contact_name?: string;
  case_name?: string;
  responsible_person_name?: string;
  invoices: FinanceAttachment[];  // 发票列表
}

export interface AllFinanceRecordsResponse {
  items: FinanceRecordWithDetails[];
  total: number;
}

export const getAllFinanceRecords = async (
  params?: GetAllFinanceRecordsParams
): Promise<AllFinanceRecordsResponse> => {
  try {
    return await get<AllFinanceRecordsResponse>('/finance/records', { params });
  } catch (error: any) {
    console.error('Failed to get all finance records:', error);
    throw error;
  }
};

/**
 * Get finance statistics
 * @returns Finance statistics
 */
export interface FinanceStatistics {
  total_records: number;
  lead_records: number;
  case_records: number;
  today_records: number;
}

export const getFinanceStatistics = async (): Promise<FinanceStatistics> => {
  try {
    return await get<FinanceStatistics>('/finance/statistics');
  } catch (error: any) {
    console.error('Failed to get finance statistics:', error);
    throw error;
  }
};

/**
 * Get finance record creators
 * Returns list of users who have created finance records
 */
export interface FinanceRecordCreator {
  id: number;
  real_name: string;
}

export const getFinanceRecordCreators = async (): Promise<FinanceRecordCreator[]> => {
  try {
    return await get<FinanceRecordCreator[]>('/finance/record-creators');
  } catch (error: any) {
    console.error('Failed to get finance record creators:', error);
    throw error;
  }
};

/**
 * Update finance record remark
 * @param recordId - Finance record ID
 * @param remark - New remark content
 * @returns Updated record info
 */
export const updateFinanceRemark = async (
  recordId: number,
  remark: string
): Promise<{ id: number; remark: string; message: string }> => {
  try {
    return await patch<{ id: number; remark: string; message: string }>(
      `/finance/records/${recordId}/remark`,
      { remark }
    );
  } catch (error: any) {
    console.error('Failed to update finance remark:', error);
    throw error;
  }
};
